import {
    Zap,
    TrendingUp,
    ArrowRight,
    AlertCircle,
    ShieldCheck,
    Coins,
    Wallet,
    X,
    Clock,
    CheckCircle2,
    Receipt
} from 'lucide-react';
import React, { useState, useEffect, useMemo } from 'react';
import { cn } from '@/shared/lib/utils';
import { useUserAssets } from '@/shared/hooks/useUserAssets';
import { useStaking, StakingProduct, UserStaking } from '@/shared/hooks/useStaking';
import { useAuth } from '@/shared/hooks/useAuth';
import { useKYC } from '@/shared/hooks/useKYC';
import { useNavigate } from 'react-router-dom';
import TradingSuspendedModal from '@/shared/components/TradingSuspendedModal';

const StakingPage: React.FC = () => {
    const { user, profile } = useAuth();
    const { status: kycStatus, loading: kycLoading } = useKYC(user?.uid);
    const navigate = useNavigate();
    const { assets, updateBalance } = useUserAssets();
    const { products, myStakings, joinStaking, cancelStaking, loading: stakingLoading } = useStaking(user?.uid);
    const [selectedProduct, setSelectedProduct] = useState<StakingProduct | null>(null);
    const [isJoining, setIsJoining] = useState(false);
    const [stakeAmount, setStakeAmount] = useState<string>('');
    const [currentTime, setCurrentTime] = useState(Date.now());
    const [showTradingSuspendedModal, setShowTradingSuspendedModal] = useState(false);

    // 실시간 시간 업데이트 (보상 계산용)
    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    // 보상 계산 유틸리티
    const calculateReward = (staking: UserStaking) => {
        const startTime = staking.stakedAt.toDate().getTime();
        const diffInSeconds = (currentTime - startTime) / 1000;
        const yearInSeconds = 365 * 24 * 60 * 60;
        const reward = (staking.amount * (staking.apy / 100) * diffInSeconds) / yearInSeconds;
        return reward;
    };

    const totalActiveStaked = useMemo(() => {
        if (!myStakings) return 0;
        return myStakings.filter(s => s.status === 'Active').reduce((acc, s) => acc + s.amount, 0);
    }, [myStakings]);

    const totalEstimatedRewards = useMemo(() => {
        if (!myStakings) return 0;
        return myStakings.filter(s => s.status === 'Active').reduce((acc, s) => acc + calculateReward(s), 0);
    }, [myStakings, currentTime]);

    const handleJoin = async () => {
        if (!user) {
            alert('로그인이 필요합니다.');
            return;
        }
        // 🚨 거래정지 확인
        if (profile?.tradingSuspended) {
            setShowTradingSuspendedModal(true);
            return;
        }
        if (!selectedProduct || !assets) return;
        const amount = parseFloat(stakeAmount);

        if (isNaN(amount) || amount < selectedProduct.minAmount) {
            alert(`최소 참여 금액(${selectedProduct.minAmount} ${selectedProduct.asset}) 이상을 입력해주세요.`);
            return;
        }

        // 잔액 체크 (KRW 기반 시스템인 경우 KRW 체크, 코인 기반인 경우 해당 코인 체크)
        // 여기서는 유저의 요청에 따라 자산별 체크 로직 구현
        const assetKey = selectedProduct.asset.toLowerCase() as keyof typeof assets;
        const currentBalance = (assets[assetKey] as number) || 0;

        if (currentBalance < amount) {
            alert(`잔액이 부족합니다. (현재 ${selectedProduct.asset} 잔액: ${currentBalance})`);
            return;
        }

        if (!confirm(`${selectedProduct.name}에 ${amount} ${selectedProduct.asset}을 스테이킹 하시겠습니까?`)) return;

        setIsJoining(true);
        try {
            await joinStaking(selectedProduct, amount);
            await updateBalance(assetKey, -amount);
            alert('스테이킹 참여가 완료되었습니다.');
            setSelectedProduct(null);
            setStakeAmount('');
        } catch (error) {
            console.error(error);
            alert('참여 중 오류가 발생했습니다.');
        } finally {
            setIsJoining(false);
        }
    };

    if (stakingLoading || kycLoading) {
        return <div className="min-h-screen flex items-center justify-center">
            <Zap className="h-12 w-12 text-blue-500 animate-spin" />
        </div>;
    }


    const activeProducts = products.filter(p => p.status === 'Active');

    return (
        <div className="min-h-screen pb-24 text-foreground animate-in fade-in duration-700">
            <div className="max-w-6xl mx-auto px-4 lg:px-8 space-y-12">

                {/* Header Section */}
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 pt-8">
                    <div className="space-y-4 max-w-2xl">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-500 transition-colors">
                            <Zap className="h-3.5 w-3.5 fill-blue-500" />
                            <span className="text-[10px] font-black uppercase tracking-[0.2em]">DeFi Staking Hub</span>
                        </div>
                        <h1 className="text-4xl md:text-5xl font-black tracking-tighter leading-[0.9] uppercase">
                            안정적인 자산 증식,<br />
                            <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 dark:from-blue-500 dark:via-indigo-500 dark:to-purple-500">
                                스테이킹의 새로운 기준
                            </span>
                        </h1>
                        <p className="text-muted-foreground font-bold text-sm md:text-base leading-relaxed max-w-lg uppercase tracking-tight">
                            자산의 안전은 지키면서, 시장 평균 이상의 수익률을 누리세요.<br />
                            실시간으로 쌓이는 보상을 확인하고 복리 효과를 극대화하세요.
                        </p>
                    </div>

                    {/* My Participation Summary Card */}
                    <div className="p-8 rounded-[2.5rem] bg-card border border-border shadow-2xl relative overflow-hidden group min-w-[350px] transition-colors">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 blur-3xl rounded-full"></div>
                        <div className="relative z-10 space-y-6">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest italic">Live Interests</span>
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 font-black text-[9px] uppercase tracking-widest">
                                    <Clock className="h-3 w-3 animate-pulse" /> Real-time
                                </div>
                            </div>
                            <div>
                                <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1">총 누적 예상 보상</p>
                                <p className="text-4xl font-black text-emerald-500 tracking-tighter tabular-nums leading-none">
                                    {totalEstimatedRewards.toFixed(8)}
                                </p>
                            </div>
                            <div className="pt-4 border-t border-border/50 flex items-center justify-between">
                                <div>
                                    <p className="text-[9px] font-black text-muted-foreground uppercase">총 예치 자산</p>
                                    <p className="text-lg font-black text-foreground">{totalActiveStaked.toLocaleString()} Assets</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-[9px] font-black text-muted-foreground uppercase">참여중인 플랜</p>
                                    <p className="text-lg font-black text-foreground">{myStakings.filter(s => s.status === 'Active').length} Plans</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Active Stakings List */}
                {myStakings.filter(s => s.status === 'Active').length > 0 && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-5 duration-700">
                        <div className="flex items-center justify-between px-2">
                            <h3 className="text-lg font-black text-foreground uppercase tracking-tighter italic flex items-center gap-2">
                                <Receipt className="h-5 w-5 text-blue-500" />
                                나의 활성 스테이킹 (Active)
                            </h3>
                            <span className="text-[10px] font-black text-muted-foreground uppercase tracking-widest italic">Manage your investments</span>
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                            {myStakings.filter(s => s.status === 'Active').map((staking) => (
                                <div key={staking.id} className="p-6 rounded-[2rem] bg-card border border-border hover:border-blue-500/20 transition-all flex flex-col md:flex-row md:items-center justify-between gap-6 shadow-lg group">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-500 font-black text-sm">
                                            {staking.asset}
                                        </div>
                                        <div>
                                            <p className="font-black text-foreground line-clamp-1">{staking.productName}</p>
                                            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mt-0.5">
                                                Staked at: {staking.stakedAt?.toDate().toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 md:flex md:items-center gap-8 md:gap-12">
                                        <div className="space-y-1">
                                            <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">예치 수량</p>
                                            <p className="font-black text-foreground tabular-nums">{staking.amount.toLocaleString()} <span className="text-[10px] text-muted-foreground">{staking.asset}</span></p>
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest text-right md:text-left">수익률 (APY)</p>
                                            <p className="font-black text-emerald-500 tabular-nums text-right md:text-left">{staking.apy}%</p>
                                        </div>
                                    </div>

                                    <button
                                        onClick={async () => {
                                            const refundEstimate = staking.amount * 0.85;
                                            if (window.confirm(`[중도 해지 안내]\n본 상품을 중도 해지하시겠습니까?\n\n- 중도 해지 수수료: 15% (원금의 15%가 공제됩니다)\n- 예상 환불 금액: ${refundEstimate.toLocaleString()} ${staking.asset} (원금의 85%)\n\n계속하시려면 확인을 눌러주세요.`)) {
                                                await cancelStaking(staking);
                                                alert(`스테이킹이 취소되었습니다. 수수료 15%를 제외한 원금의 85%(${refundEstimate.toLocaleString()} ${staking.asset})가 지갑으로 환불되었습니다.`);
                                            }
                                        }}
                                        className="h-12 px-8 rounded-xl bg-accent hover:bg-red-500/10 hover:text-red-500 text-[10px] font-black uppercase tracking-widest transition-all border border-border active:scale-95"
                                    >
                                        취소/환불 요청
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Staking Products Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {activeProducts.map((product) => (
                        <div
                            key={product.id}
                            className="group p-8 rounded-[3rem] bg-card border border-border hover:border-blue-500/30 transition-all duration-500 relative overflow-hidden flex flex-col justify-between h-full shadow-2xl"
                        >
                            <div className="absolute top-0 right-0 w-40 h-40 bg-foreground/[0.01] blur-3xl rounded-full group-hover:bg-blue-500/[0.03] transition-colors"></div>

                            <div className="relative z-10 space-y-8">
                                <div className="flex items-center justify-between">
                                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center border border-border shadow-inner bg-blue-500/10 text-blue-500 transition-transform group-hover:scale-110 duration-500">
                                        <TrendingUp className="h-7 w-7" />
                                    </div>
                                    <div className="px-3 py-1 rounded-full bg-accent border border-border text-[9px] font-black text-muted-foreground uppercase tracking-widest">
                                        {product.duration === 0 ? 'Flexible' : `${product.duration} Days`}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <h3 className="text-2xl font-black text-foreground uppercase tracking-tight">{product.name}</h3>
                                    <p className="text-xs font-bold text-muted-foreground leading-relaxed uppercase h-8 overflow-hidden line-clamp-2">
                                        {product.description}
                                    </p>
                                </div>

                                <div className="p-6 rounded-[2rem] bg-foreground/[0.02] border border-border space-y-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-black text-muted-foreground uppercase tracking-tighter italic">APR / APY</span>
                                        <span className="text-2xl font-black text-emerald-500 leading-none tabular-nums">{product.apy}%</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-[10px] font-black text-muted-foreground uppercase tracking-tighter italic">Min. Deposit</span>
                                        <span className="text-sm font-black text-foreground tabular-nums">{product.minAmount} {product.asset || ''}</span>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => setSelectedProduct(product)}
                                className="relative z-10 w-full h-16 mt-10 rounded-[1.5rem] bg-blue-600 hover:bg-blue-500 text-white font-black text-[11px] uppercase tracking-[0.2em] shadow-xl transition-all hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-3 overflow-hidden group/btn"
                            >
                                <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700"></div>
                                <span className="relative z-10">참여하기</span>
                                <ArrowRight className="h-4 w-4 relative z-10 group-hover/btn:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    ))}
                </div>

                {/* Participation Details Modal */}
                {selectedProduct && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
                        <div className="bg-card border border-border w-full max-w-lg rounded-[2.5rem] overflow-hidden shadow-2xl">
                            <div className="px-8 py-6 border-b border-border flex items-center justify-between bg-accent/30">
                                <div>
                                    <h3 className="text-xl font-black text-foreground uppercase tracking-tight">{selectedProduct.name}</h3>
                                    <p className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.2em]">Staking Participation</p>
                                </div>
                                <button onClick={() => setSelectedProduct(null)} className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-full transition-all">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="p-8 space-y-8">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-5 rounded-2xl bg-accent/20 border border-border space-y-1">
                                        <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">예상 수익률 (APY)</p>
                                        <p className="text-2xl font-black text-emerald-500 tabular-nums">{selectedProduct.apy}%</p>
                                    </div>
                                    <div className="p-5 rounded-2xl bg-accent/20 border border-border space-y-1">
                                        <p className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">최소 예치 수량</p>
                                        <p className="text-2xl font-black text-foreground tabular-nums">{selectedProduct.minAmount} <span className="text-xs">{selectedProduct.asset || ''}</span></p>
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <div className="flex items-center justify-between px-2">
                                        <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground italic">예치 수량 (Amount)</label>
                                        <span className="text-[9px] font-bold text-muted-foreground uppercase">Balance: {(assets?.[(selectedProduct.asset || '').toLowerCase() as keyof typeof assets] || 0).toLocaleString()} {selectedProduct.asset || ''}</span>
                                    </div>
                                    <div className="relative">
                                        <input
                                            type="number"
                                            value={stakeAmount}
                                            onChange={(e) => setStakeAmount(e.target.value)}
                                            className="w-full bg-accent/30 border border-border rounded-2xl px-6 py-4 text-xl font-black tabular-nums focus:outline-none focus:border-blue-500 transition-colors"
                                            placeholder="0.00"
                                        />
                                        <div className="absolute right-6 top-1/2 -translate-y-1/2 font-black text-muted-foreground uppercase tracking-widest text-xs">
                                            {selectedProduct.asset || ''}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        {[25, 50, 75, 100].map(pct => (
                                            <button
                                                key={pct}
                                                onClick={() => {
                                                    const bal = assets?.[(selectedProduct.asset || '').toLowerCase() as keyof typeof assets] as number || 0;
                                                    setStakeAmount((bal * pct / 100).toString());
                                                }}
                                                className="flex-1 py-2 rounded-lg bg-accent/50 border border-border text-[10px] font-black text-muted-foreground hover:bg-blue-500 hover:text-white transition-all uppercase"
                                            >
                                                {pct}%
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="p-6 rounded-2xl bg-blue-500/5 border border-blue-500/10 space-y-3">
                                    <p className="text-[10px] font-black text-blue-600 dark:text-blue-500 uppercase tracking-widest flex items-center gap-2">
                                        <AlertCircle className="h-3 w-3" /> 유의사항
                                    </p>
                                    <p className="text-xs font-bold text-muted-foreground/80 leading-relaxed uppercase tracking-tight">
                                        스테이킹 예치 시 해당 자산은 지갑에서 차감되며, 약정 기간({selectedProduct.duration === 0 ? '자유' : `${selectedProduct.duration}일`}) 동안 보상이 실시간으로 누적됩니다.
                                    </p>
                                </div>

                                <button
                                    onClick={handleJoin}
                                    disabled={isJoining}
                                    className="w-full py-5 bg-blue-600 hover:bg-blue-500 text-white rounded-[1.5rem] font-black uppercase tracking-[0.2em] shadow-xl transition-all active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50"
                                >
                                    {isJoining ? <Zap className="h-5 w-5 animate-spin" /> : <ShieldCheck className="h-5 w-5" />}
                                    스테이킹 참여 확정
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Important Notes */}
                <div className="p-10 rounded-[3rem] bg-card border border-border relative overflow-hidden group">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/[0.02] blur-3xl rounded-full"></div>
                    <div className="relative z-10 space-y-8">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center text-amber-500">
                                <AlertCircle className="h-6 w-6" />
                            </div>
                            <div>
                                <h4 className="text-lg font-black text-foreground uppercase tracking-tighter">참여 전 필수 확인 사항</h4>
                                <p className="text-[10px] font-black text-amber-600 dark:text-amber-500/70 uppercase tracking-widest">Read carefully before participating</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[
                                { id: '01', title: '해지 정책', text: '스테이킹 시작 후 약정 기간 동안은 원칙적으로 해지가 불가능하며, 부득이한 해지 시 중도 해지 수수료가 발생할 수 있습니다.' },
                                { id: '02', title: '보상 정산', text: '보상은 예치 즉시 초 단위로 계산되어 내 대시보드에서 실시간으로 확인 및 추적할 수 있습니다.' },
                                { id: '03', title: '자산 안전', text: '예치된 자산은 콜드 월렛 기반의 안전한 보안 원칙에 따라 분리 보관되어 원금 손실로부터 안전하게 보호됩니다.' },
                            ].map((note) => (
                                <div key={note.id} className="p-6 rounded-2xl bg-foreground/[0.02] border border-border space-y-3 hover:bg-foreground/[0.04] transition-colors">
                                    <span className="text-lg font-black text-muted-foreground/20 italic">{note.id}</span>
                                    <h5 className="font-black text-foreground text-sm uppercase">{note.title}</h5>
                                    <p className="text-xs font-bold text-muted-foreground leading-relaxed uppercase tracking-tighter">{note.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex flex-col items-center justify-center py-10 gap-4 opacity-50">
                    <ShieldCheck className="h-6 w-6 text-muted-foreground" />
                    <p className="text-[10px] font-black text-muted-foreground uppercase tracking-[0.3em]">cointocash 원장 프로토콜에 의해 보호됨</p>
                </div>
            </div>

            {/* 거래정지 모달 */}
            {showTradingSuspendedModal && (
                <TradingSuspendedModal onClose={() => setShowTradingSuspendedModal(false)} />
            )}
        </div>
    );
};

export default StakingPage;
